import axios from 'axios'

// Environment configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'
const API_TIMEOUT = import.meta.env.VITE_API_TIMEOUT || 10000
const AUTH_TOKEN_KEY = import.meta.env.VITE_AUTH_TOKEN_KEY || 'auth_token'
// Disable login redirect on 401 for local development (set VITE_DISABLE_LOGIN_REDIRECT=true)
const DISABLE_LOGIN_REDIRECT = (import.meta.env.VITE_DISABLE_LOGIN_REDIRECT === 'true')

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY)
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // In development or when explicitly disabled, do NOT redirect to /login
      if (!DISABLE_LOGIN_REDIRECT) {
        localStorage.removeItem(AUTH_TOKEN_KEY)
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

// Dashboard API functions
export const fetchDashboardData = async () => {
  const endpoint = import.meta.env.VITE_DASHBOARD_SUMMARY_ENDPOINT || '/dashboard/summary'
  const response = await api.get(endpoint)
  return response.data
}

export const fetchHotelStatus = async () => {
  const endpoint = import.meta.env.VITE_HOTELS_STATUS_ENDPOINT || '/hotels/status'
  const response = await api.get(endpoint)
  return response.data
}

export const fetchHotelDetails = async (hotelId) => {
  const baseEndpoint = import.meta.env.VITE_HOTELS_DETAILS_ENDPOINT || '/hotels'
  const response = await api.get(`${baseEndpoint}/${hotelId}/details`)
  return response.data
}

// Add new dashboard metrics function
export const fetchDashboardMetrics = async (period = '7d') => {
  const endpoint = import.meta.env.VITE_DASHBOARD_METRICS_ENDPOINT || '/dashboard/metrics'
  const response = await api.get(`${endpoint}?period=${period}`)
  return response.data
}

// Xpert API monitoring functions
export const fetchXpertLogs = async (endpoint, limit = 50) => {
  const baseEndpoint = import.meta.env.VITE_XPERT_LOGS_ENDPOINT || '/xpert/logs'
  const defaultLimit = import.meta.env.VITE_DEFAULT_LIMIT || 50
  const actualLimit = limit || defaultLimit
  
  let url = baseEndpoint
  if (endpoint) {
    url += `/${endpoint}`
  }
  url += `?limit=${actualLimit}`
  
  const response = await api.get(url)
  // Return the actual data array from the API response
  return response.data?.data || []
}

export const fetchXpertStats = async (endpoint, timeRange = '24h') => {
  const baseEndpoint = import.meta.env.VITE_XPERT_STATS_ENDPOINT || '/xpert/stats'
  
  let url = baseEndpoint
  if (endpoint) {
    url += `/${endpoint}`
  }
  url += `?range=${timeRange}`
  
  const response = await api.get(url)
  // Return the actual data from the API response
  return response.data?.data || {}
}

// Booking API functions
export const fetchBookingSummary = async () => {
  const endpoint = import.meta.env.VITE_BOOKING_SUMMARY_ENDPOINT || '/bookings/summary'
  const response = await api.get(endpoint)
  return response.data
}

export const fetchBookings = async (filters = {}) => {
  const endpoint = import.meta.env.VITE_BOOKINGS_ENDPOINT || '/bookings'
  const params = new URLSearchParams()

  // Map FE filters to BE parameters
  const mapFeStatusToApi = (s) => {
    const v = (s || '').toLowerCase()
    if (v === 'confirmed') return 'scheduled'
    if (['pending', 'cancelled', 'scheduled', 'completed', 'processing', 'paid'].includes(v)) return v
    return 'all'
  }

  if (filters.date_from) params.append('date_from', filters.date_from)
  if (filters.date_to) params.append('date_to', filters.date_to)
  if (filters.property_id && filters.property_id !== 'all') params.append('property_id', filters.property_id)

  const mappedStatus = mapFeStatusToApi(filters.status)
  if (mappedStatus && mappedStatus !== 'all') params.append('status', mappedStatus)

  if (filters.search) params.append('search', filters.search)
  if (filters.tab) params.append('tab', filters.tab)
  if (filters.per_page) params.append('per_page', String(filters.per_page))

  const url = params.toString() ? `${endpoint}?${params}` : endpoint
  const response = await api.get(url)
  const payload = response.data
  return {
    bookings: payload?.data || [],
    meta: payload?.meta || {}
  }
}

export const fetchBookingStats = async (filters = {}) => {
  const endpoint = import.meta.env.VITE_BOOKING_STATS_ENDPOINT || '/bookings/stats'
  const response = await api.get(endpoint, { params: filters })
  return response.data
}

export const fetchHotelsForBooking = async () => {
  // Use hotel status endpoint (more likely public) to get hotel list
  const endpoint = import.meta.env.VITE_HOTELS_STATUS_ENDPOINT || '/hotels/status'
  const response = await api.get(endpoint)
  const payload = response.data

  // Normalize various possible shapes into an array
  let hotels = []
  if (Array.isArray(payload)) {
    hotels = payload
  } else if (payload?.hotels) {
    if (Array.isArray(payload.hotels)) {
      hotels = payload.hotels
    } else if (Array.isArray(payload.hotels.data)) {
      hotels = payload.hotels.data
    } else if (typeof payload.hotels === 'object') {
      const hotelValues = Object.values(payload.hotels)
      const hotelArray = hotelValues.find(val => Array.isArray(val))
      if (hotelArray) hotels = hotelArray
    }
  } else if (Array.isArray(payload?.data)) {
    hotels = payload.data
  }

  // Normalize to { id, name } — prefer property_id when available for bookings filter
  return hotels.map((row) => ({ id: row.property_id || row.id, name: row.title || row.name || `Hotel #${row.id}` }))
}

export default api